import React, { useEffect, useState, useRef } from "react";
import { EarningsCalendarItem } from "../api/models/EarningsCalendar";
import "react-calendar/dist/Calendar.css";
import { navigationRoutes } from "../constants/constants";
import { Dialog, DialogPanel } from "@headlessui/react";
import { Bars3Icon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

export const EarningsCalendarComponent = ({
    calendar,
}: {
    calendar: EarningsCalendarItem[];
}) => {
    if (window.location.pathname === "/earnings-calendar") {
        document.title = "Earnings Calendar | PocketQuant";
    }
    const today = new Date().toISOString().split("T")[0];
    const [selectedDay, setSelectedDay] = useState<string | null>(today);
    const [startIndex, setStartIndex] = useState(0);
    const [selectedTickers, setSelectedTickers] = useState<string[]>([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const dateScrollerRef = useRef<HTMLDivElement>(null);

    const visibleDatesCount = 5; // Number of dates to show in mobile view

    // Update selectedTickers whenever calendar or selectedDay changes
    useEffect(() => {
        if (calendar && calendar.length > 0 && selectedDay) {
            const dayItem = calendar.find(e => e.date === selectedDay);
            if (dayItem) {
                setSelectedTickers(dayItem.tickers);
            }
        }
    }, [calendar, selectedDay]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const todayIndex = calendar.findIndex(item => item.date === today);
        if (todayIndex !== -1) {
            setStartIndex(Math.max(0, todayIndex - Math.floor(visibleDatesCount / 2)));
        }
    }, [calendar, today]);

    const handleDayClicked = (item: EarningsCalendarItem) => {
        if (item.date === selectedDay) {
            return; // Keep selection on mobile
        }
        setSelectedDay(item.date);
        setSelectedTickers(item.tickers);
    };

    const getDay = (date: string) => parseInt(date.split("-")[2]);

    const getDayName = (date: string) => {
        const [year, month, day] = date.split("-").map(Number);
        const d = new Date(year, month - 1, day);
        const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        return dayNames[d.getDay()];
    };

    const navigateDates = (direction: 'prev' | 'next') => {
        if (direction === 'prev') {
            setStartIndex(Math.max(0, startIndex - 1));
        } else {
            setStartIndex(Math.min(calendar.length - visibleDatesCount, startIndex + 1));
        }
    };

    const formatDateForDisplay = (date: string) => {
        const day = getDay(date);
        const dayName = getDayName(date)?.substring(0, 3);
        return `${dayName} ${day}`;
    };

    const visibleDates = calendar.slice(startIndex, startIndex + visibleDatesCount);

    return (
        <div className="calendar-workspace h-screen flex flex-col overflow-hidden">
            {/* Desktop View */}
            <div className={`${isMobile ? 'hidden' : 'block'}`}>
                <div className="grid grid-cols-7 gap-2 p-4">
                    {calendar.map((item) => {
                        const isSelected = item.date === selectedDay;
                        return (
                            <div
                                key={item.date}
                                onClick={() => handleDayClicked(item)}
                                className={`cursor-pointer p-4 border rounded-lg ${
                                    item.tickers.length === 0
                                        ? "bg-gray-100 text-gray-500"
                                        : "bg-white text-black"
                                } ${isSelected ? "ring-2 ring-orange-500" : ""}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="text-lg font-bold">{getDay(item.date)}</div>
                                    <div className="text-sm">{getDayName(item.date)?.substring(0, 3)}</div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="text-sm whitespace-pre-wrap">{item.tickers.length}</div>
                                    <div className="text-sm xl:block overflow-hidden text-ellipsis whitespace-nowrap sm:max-w-[100px] md:max-w-[150px]">
                                        {item.tickers.slice(0, 3).join(", ")}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Mobile View */}
            <div className={`${isMobile ? 'flex flex-col h-full' : 'hidden'}`}>
                {/* Date selector bar */}
                <div className="px-2 py-3 border-b sticky top-0 z-10">
                    <div className="flex items-center">
                        <button 
                            onClick={() => navigateDates('prev')} 
                            disabled={startIndex === 0}
                            className={`p-2 ${startIndex === 0 ? 'text-gray-300' : 'text-gray-700'}`}
                        >
                            <ChevronLeftIcon className="h-5 w-5" />
                        </button>
                        
                        <div ref={dateScrollerRef} className="flex-1 overflow-hidden">
                            <div className="flex justify-between">
                                {visibleDates.map((item) => {
                                    const isSelected = item.date === selectedDay;
                                    return (
                                        <div
                                            key={item.date}
                                            onClick={() => handleDayClicked(item)}
                                            className={`flex-1 mx-1 cursor-pointer py-2 px-1 text-center rounded-lg ${
                                                item.tickers.length === 0
                                                    ? "bg-gray-100 text-gray-500"
                                                    : "bg-white text-black"
                                            } ${isSelected ? "bg-orange-100 border-2 border-orange-500" : "border"}`}
                                        >
                                            <div className="text-sm font-medium">{formatDateForDisplay(item.date)}</div>
                                            <div className="text-xs">{item.tickers.length} companies</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        
                        <button 
                            onClick={() => navigateDates('next')} 
                            disabled={startIndex >= calendar.length - visibleDatesCount}
                            className={`p-2 ${startIndex >= calendar.length - visibleDatesCount ? 'text-gray-300' : 'text-gray-700'}`}
                        >
                            <ChevronRightIcon className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Earnings List for Mobile */}
                {selectedDay && (
                    <div className="flex-1 overflow-y-auto p-3 bg-gray-50">
                        <h2 className="text-lg font-semibold mb-3 text-gray-800">
                            Earnings for {getDayName(selectedDay)}, {selectedDay}
                        </h2>
                        {selectedTickers.length > 0 ? (
                            <ul className="divide-y divide-gray-200">
                                {selectedTickers.sort().map((ticker) => (
                                    <li
                                        key={ticker}
                                        className="py-3 px-4 bg-white hover:bg-gray-100 rounded-md shadow-sm my-1"
                                    >
                                        <span className="text-sm font-medium text-gray-700">{ticker}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-gray-500 text-center p-4">No earnings reported for this day.</p>
                        )}
                    </div>
                )}
            </div>

            {/* Earnings List for Desktop */}
            {!isMobile && selectedDay && (
                <div className="h-[40%] overflow-y-auto p-6 border-t bg-gray-50 rounded-t-lg">
                    <h2 className="text-lg font-semibold mb-4 text-gray-800">
                        Earnings for {getDayName(selectedDay)}, {selectedDay}
                    </h2>
                    {selectedTickers.length > 0 ? (
                        <ul className="divide-y divide-gray-200">
                            {selectedTickers.sort().map((ticker) => (
                                <li
                                    key={ticker}
                                    className="flex items-center justify-between py-2 px-4 bg-white hover:bg-gray-100 rounded-md shadow-sm transition-all duration-150"
                                >
                                    <span className="text-sm font-medium text-gray-700">{ticker}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-gray-500">No earnings reported for this day.</p>
                    )}
                </div>
            )}
        </div>
    );
};
